import React, { useEffect, useRef, useState } from 'react';
import { 
  FaEnvelope,
  FaMapMarkerAlt,
  FaRegCopyright,
  FaArrowUp
} from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

const Footer = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { currentTheme } = useTheme();
  const { currentLanguage } = useLanguage();
  
  // Настройки цветовой темы интерфейса
  const themeClasses = {
    logo: currentTheme === 'light'
      ? 'from-light-primary to-light-secondary'
      : 'from-[#00F0FF] to-[#FF00F5]'
  };
  
  // Установка текущего года
  useEffect(() => {
    setYear(new Date().getFullYear());
  }, []);
  
  // Анимация появления при скролле
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    
    if (footerRef.current) {
      observer.observe(footerRef.current);
    }
    
    return () => {
      if (footerRef.current) {
        observer.unobserve(footerRef.current);
      }
    };
  }, []);
  
  // Кнопка прокрутки наверх
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setShowScrollTop(scrollPosition > 500);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Обработка формы подписки
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Проверка валидности email
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!email || !emailRegex.test(email)) {
      setIsEmailValid(false);
      return;
    }
    
    // Подготовка данных для отправки в Web3Forms
    const formData = {
      email: email,
      subject: "Подписка на новости MoviBobby",
      from_name: "Подписчик на новости",
      message: "Запрос на подписку на новости",
      access_key: "65843dcd-2b63-43b1-8a2f-f2941ae9782d"
    };
    
    // Отправка данных на сервер Web3Forms
    fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(async (response) => {
      if (response.ok) {
        // Успешная отправка
        setIsEmailValid(true);
        setIsSubscribed(true);
        setEmail('');
        
        // Сброс состояния через 5 секунд
        setTimeout(() => {
          setIsSubscribed(false);
        }, 5000);
      } else {
        const errorResponse = await response.json();
        console.error('Error subscribing:', errorResponse);
        // Показать ошибку пользователю
      }
    })
    .catch((error) => {
      console.error('Error during form submission:', error);
      // Показать ошибку пользователю
    });
  };
  
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setIsEmailValid(true);
  };
  
  // Данные для футера
  const contactInfo = [
    { icon: <FaEnvelope />, text: 'info@movibobby.com', url: 'mailto:info@movibobby.com' }
  ];
  
  const quickLinks = [
    { text: currentLanguage === 'en' ? 'Home' : 'Главная', url: '#home' },
    { text: currentLanguage === 'en' ? 'About' : 'О нас', url: '#about' },
    { text: currentLanguage === 'en' ? 'Services' : 'Услуги', url: '#services' },
    { text: currentLanguage === 'en' ? 'Tech Stack' : 'Технологии', url: '#tech-stack' },
    { text: currentLanguage === 'en' ? 'Work Process' : 'Процесс работы', url: '#work-process' },
    { text: currentLanguage === 'en' ? 'Contact' : 'Контакты', url: '#contact' }
  ];
  
  // Translations
  const translations = {
    ru: {
      about: "Создаем цифровые решения, которые помогают бизнесу расти и развиваться в современной конкурентной среде.",
      quickLinks: "Быстрые ссылки",
      contactUs: "Связаться с нами",
      subscribe: "Подписаться на новости",
      subscribeText: "Подписывайтесь на нашу рассылку, чтобы получать последние новости и обновления",
      emailPlaceholder: "Ваш email",
      subscribeButton: "Подписаться",
      emailRequired: "Введите корректный email",
      thankYou: "Спасибо за подписку!",
      copyright: "Все права защищены.",
      privacy: "Политика конфиденциальности",
      terms: "Условия использования"
    },
    en: {
      about: "We create digital solutions that help businesses grow and thrive in today's competitive environment.",
      quickLinks: "Quick Links",
      contactUs: "Contact Us",
      subscribe: "Subscribe to Our Newsletter",
      subscribeText: "Subscribe to our newsletter to receive the latest news and updates",
      emailPlaceholder: "Your email",
      subscribeButton: "Subscribe",
      emailRequired: "Please enter a valid email",
      thankYou: "Thank you for subscribing!",
      copyright: "All rights reserved.",
      privacy: "Privacy Policy",
      terms: "Terms of Service"
    }
  };

  // Get translated text
  const getText = (key) => translations[currentLanguage]?.[key] || translations.ru[key];
  
  // Scroll to section function
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      window.scrollTo({
        top: section.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };
  
  return (
    <footer 
      ref={footerRef} 
      className={`relative pt-16 pb-10 overflow-hidden ${
        currentTheme === 'light' ? 'bg-white' : 'bg-[#070707]'
      }`}
    >
      {/* Кнопка прокрутки наверх */}
      <button 
        onClick={scrollToTop} 
        className={`fixed bottom-8 right-8 w-12 h-12 rounded-full flex items-center justify-center text-white z-50 shadow-lg transition-all duration-300 ${
          currentTheme === 'light'
            ? 'bg-gradient-to-r from-light-primary to-light-secondary'
            : 'bg-gradient-to-r from-[#00F0FF] to-[#FF00F5]'
        } ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
      >
        <FaArrowUp />
      </button>
      
      {/* Декоративная верхняя линия */}
      <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${
        currentTheme === 'light'
          ? 'from-light-primary to-light-secondary'
          : 'from-[#00F0FF] to-[#FF00F5]'
      }`}></div>
      
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* О компании */}
          <div 
            className={`transition-all duration-500 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <Link to="/" className={`text-2xl font-bold mb-6 block bg-clip-text text-transparent bg-gradient-to-r ${themeClasses.logo}`}>MoviBobby</Link>
            <p className={`mb-6 ${
              currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'
            }`}>
              {getText('about')}
            </p>
          </div>
          
          {/* Навигация */}
          <div 
            className={`transition-all duration-500 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '100ms' }}
          >
            <h3 className={`text-xl font-bold mb-6 ${
              currentTheme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>{getText('quickLinks')}</h3>
            <ul className="space-y-4">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.url}
                    onClick={(e) => {
                      e.preventDefault();
                      if (link.url && link.url.substring(1)) {
                        scrollToSection(link.url.substring(1));
                      }
                    }}
                    className={`transition-all duration-300 inline-block hover:translate-x-2 transform ${
                      currentTheme === 'light'
                        ? 'text-gray-600 hover:text-light-primary'
                        : 'text-gray-400 hover:text-[#00F0FF]'
                    }`}
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Контактная информация */}
          <div 
            className={`transition-all duration-500 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <h3 className={`text-xl font-bold mb-6 ${
              currentTheme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>{getText('contactUs')}</h3>
            <ul className="space-y-4">
              {contactInfo.map((item, index) => (
                <li key={index} className="flex items-start">
                  <div className={`mt-1 mr-3 ${
                    currentTheme === 'light' ? 'text-light-primary' : 'text-[#00F0FF]'
                  }`}>{item.icon}</div>
                  <a 
                    href={item.url} 
                    className={`transition-colors duration-300 ${
                      currentTheme === 'light'
                        ? 'text-gray-600 hover:text-light-primary'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Подписка */}
          <div 
            className={`transition-all duration-500 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            <h3 className={`text-xl font-bold mb-6 ${
              currentTheme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>{getText('subscribe')}</h3>
            <p className={`mb-4 ${
              currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'
            }`}>
              {getText('subscribeText')}
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col space-y-3">
              <input type="hidden" name="access_key" value="65843dcd-2b63-43b1-8a2f-f2941ae9782d" />
              <input 
                type="email" 
                value={email}
                onChange={handleEmailChange}
                placeholder={getText('emailPlaceholder')} 
                className={`px-4 py-2 rounded-lg outline-none transition-colors duration-300 w-full ${
                  currentTheme === 'light'
                    ? `bg-white border ${isEmailValid ? 'border-gray-200 focus:border-light-primary' : 'border-red-500'} text-gray-800`
                    : `bg-[#111] border ${isEmailValid ? 'border-[#333] focus:border-[#00F0FF]' : 'border-red-500'} text-white`
                }`}
              />
              {!isEmailValid && (
                <p className="text-red-500 text-sm mt-1">{getText('emailRequired')}</p>
              )}
              <button 
                type="submit"
                className={`px-4 py-2 rounded-lg text-white font-medium transition-all duration-300 ${
                  currentTheme === 'light'
                    ? 'bg-gradient-to-r from-light-primary to-light-secondary hover:shadow-light-primary/20 hover:shadow-lg'
                    : 'bg-gradient-to-r from-dark-primary to-dark-secondary hover:shadow-dark-primary/20 hover:shadow-lg'
                }`}
              >
                {getText('subscribeButton')}
              </button>
            </form>
            {isSubscribed && (
              <p className="text-green-600 mt-2">{getText('thankYou')}</p>
            )}
          </div>
        </div>
        
        {/* Разделитель */}
        <div className={`h-px w-full my-8 ${
          currentTheme === 'light' ? 'bg-gray-200' : 'bg-[#222]'
        }`}></div>
        
        {/* Copyright и ссылки */}
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className={`mb-4 md:mb-0 ${
            currentTheme === 'light' ? 'text-gray-500' : 'text-gray-500'
          }`}>
            © 2020 MoviBobby. {getText('copyright')}
          </div>
          
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <Link 
              to="/privacy"
              className={`${
                currentTheme === 'light' 
                  ? 'text-gray-600 hover:text-light-primary' 
                  : 'text-gray-400 hover:text-[#00F0FF]'
              } transition-colors duration-300`}
            >
              {getText('privacy')}
            </Link>
            <Link 
              to="/terms"
              className={`${
                currentTheme === 'light' 
                  ? 'text-gray-600 hover:text-light-primary' 
                  : 'text-gray-400 hover:text-[#00F0FF]'
              } transition-colors duration-300`}
            >
              {getText('terms')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 